import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AuthCard, AuthHeading, AuthError, AUTH_LINK } from './AuthLayout';
import { EyeIcon, EyeOffIcon, LoaderCircleIcon } from './icons/LucideIcons';

const Login = ({ onSwitchToForgotPassword, onSuccess }) => {
  const { login, chooseLoginShop, authNotice } = useAuth();
  const navigate = useNavigate();
  const [isDriverLogin, setIsDriverLogin] = useState(false);
  // { choices: [{ tenantId, name }], selectionToken } when the login works at several shops
  const [shopChoice, setShopChoice] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  // Opens with the reason a session just ended, if there is one (a suspended shop),
  // and shows it when it arrives after the screen is up (QA B P2-13).
  const [error, setError] = useState(authNotice || '');
  useEffect(() => {
    if (authNotice) setError(authNotice);
  }, [authNotice]);
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const finishSignIn = () => {
    // Get the logged-in user data to determine redirect
    const userData = JSON.parse(localStorage.getItem('user'));

    // Success - call onSuccess callback if provided
    if (onSuccess) {
      onSuccess();
    }

    // Redirect based on actual account type (not toggle state)
    if (userData && userData.accountType === 'driver') {
      navigate('/driver', { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  };

  const pickShop = async (tenantId) => {
    setError('');
    setLoading(true);
    const result = await chooseLoginShop(shopChoice.selectionToken, tenantId);
    setLoading(false);
    if (result.success) {
      finishSignIn();
    } else if (result.code === 'CHOICE_EXPIRED') {
      setShopChoice(null);
      setError(result.message);
    } else {
      setError(result.message || 'Login failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const credentials = isDriverLogin
        ? { username: formData.username, password: formData.password }
        : { email: formData.email, password: formData.password };

      const result = await login(credentials);

      if (result.chooseShop) {
        setShopChoice({ choices: result.chooseShop, selectionToken: result.selectionToken });
      } else if (!result.success) {
        setError(result.message || 'Login failed');
      } else {
        finishSignIn();
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fieldClass = 'input-field h-ctl-lg px-3.5 text-base sm:text-base';

  return (
    <>
      {shopChoice ? (
        <AuthCard>
          <AuthHeading title="Which shop?" sub="This login works at more than one shop." />
          <div className="flex flex-col gap-2">
            {shopChoice.choices.map((shop) => (
              <button
                key={shop.tenantId}
                type="button"
                onClick={() => pickShop(shop.tenantId)}
                disabled={loading}
                className="btn-secondary btn-lg w-full justify-start"
              >
                {shop.name}
              </button>
            ))}
          </div>
          {error && <AuthError>{error}</AuthError>}
          <button
            type="button"
            onClick={() => { setShopChoice(null); setError(''); }}
            disabled={loading}
            className={`self-start text-[13px] ${AUTH_LINK}`}
          >
            Back
          </button>
        </AuthCard>
      ) : (
        <AuthCard as="form" onSubmit={handleSubmit}>
          <AuthHeading title="Sign in" sub="Staff account" />

          {/* Staff / Driver: drivers sign in with a username and land on /driver */}
          <div className="seg flex w-full" role="group" aria-label="Account type">
            <button
              type="button"
              aria-pressed={!isDriverLogin}
              onClick={() => setIsDriverLogin(false)}
              className={`seg-item h-8 flex-1 ${!isDriverLogin ? 'seg-item-active' : ''}`}
            >
              Staff
            </button>
            <button
              type="button"
              aria-pressed={isDriverLogin}
              onClick={() => setIsDriverLogin(true)}
              className={`seg-item h-8 flex-1 ${isDriverLogin ? 'seg-item-active' : ''}`}
            >
              Driver
            </button>
          </div>

          <div>
            <label htmlFor={isDriverLogin ? 'username' : 'email'} className="input-label">
              {isDriverLogin ? 'Username' : 'Email'}
            </label>
            {isDriverLogin ? (
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                required
                className={fieldClass}
                placeholder="Your username"
                value={formData.username}
                onChange={handleInputChange}
              />
            ) : (
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className={fieldClass}
                placeholder="you@yourshop.com"
                value={formData.email}
                onChange={handleInputChange}
              />
            )}
          </div>

          <div>
            <div className="input-label flex items-center justify-between">
              <label htmlFor="password">Password</label>
              {!isDriverLogin && (
                <button
                  type="button"
                  onClick={onSwitchToForgotPassword}
                  className={AUTH_LINK}
                >
                  Forgot it?
                </button>
              )}
            </div>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                className={`${fieldClass} pr-11`}
                placeholder="Your password"
                value={formData.password}
                onChange={handleInputChange}
              />
              {/* Hold to reveal, as before */}
              <button
                type="button"
                aria-label="Hold to show password"
                onMouseDown={() => setShowPassword(true)}
                onMouseUp={() => setShowPassword(false)}
                onMouseLeave={() => setShowPassword(false)}
                onTouchStart={() => setShowPassword(true)}
                onTouchEnd={() => setShowPassword(false)}
                className="auth-eye absolute right-1.5 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-[6px] text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                tabIndex="-1"
              >
                {showPassword ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
              </button>
            </div>
          </div>

          {error && <AuthError>{error}</AuthError>}

          <button type="submit" disabled={loading} className="btn-primary btn-lg mt-1 w-full">
            {loading ? (
              <>
                <LoaderCircleIcon size={18} className="animate-spin" />
                Signing in...
              </>
            ) : (
              isDriverLogin ? 'Sign in as driver' : 'Sign in'
            )}
          </button>
        </AuthCard>
      )}

      <div className="flex flex-col items-center gap-2 text-xs text-gray-600">
        <span>Drivers: get the Bouqmark Driver app</span>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <a
            href="https://apps.apple.com/app/bouqmark-driver/id6761919291"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download Bouqmark Driver on the App Store"
          >
            <img src="/app-store-badge.svg" alt="Download on the App Store" style={{ height: '40px' }} />
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=com.twigsfloral.driver"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Get Bouqmark Driver on Google Play"
          >
            <img src="/google-play-badge.png" alt="Get it on Google Play" style={{ height: '60px' }} />
          </a>
        </div>
      </div>
    </>
  );
};

export default Login;
